<template>
  <MainLayout :title="trans('Menu')">
    <div class="flex min-h-screen flex-col">
      <div
        ref="headerNavigationContainerElement"
        class="sticky left-0 right-0 top-0 z-30 overflow-x-hidden bg-theme-background"
        style="padding-top: var(--safe-area-inset-top)"
      >
        <div
          class="-mx-1 flex justify-between px-4 pb-2 pt-4 [&>*]:mx-1"
          style="height: calc(4.25rem + 1px)"
        >
          <ADeepCircleButton
            v-if="restaurant.isHomePageEnabled"
            @click="router.push({ name: 'home' })"
          >
            <template #icon><ArrowLeftBold /></template>
          </ADeepCircleButton>
          <ADeepCircleButton
            v-else-if="restaurant.isWelcomePageEnabled"
            @click="router.push({ name: 'welcome' })"
          >
            <template #icon><ArrowLeftBold /></template>
          </ADeepCircleButton>
          <div class="flex flex-1 justify-end">
            <div
              class="relative h-10 min-w-0 overflow-hidden rounded-full bg-theme-background transition-[width]"
              :class="[searchExpanded ? 'w-full' : 'w-10']"
            >
              <input
                ref="searchInput"
                v-model="search"
                type="text"
                :placeholder="trans('Search')"
                class="block h-full w-full rounded-full border-none bg-theme-primary/10 pl-9 pr-8 text-sm placeholder-theme-text/50 transition focus:ring-0"
                @focus="searchFocused = true"
                @blur="searchFocused = false"
              />
              <div
                role="button"
                tabindex="0"
                class="absolute bottom-0 top-0 flex h-10 w-10 items-center justify-center rounded-full transition duration-75 dir-ltr:left-0 dir-rtl:right-0"
                :class="searchExpanded ? '' : 'bg-theme-primary'"
                @click="searchInput?.focus()"
              >
                <MagnifyingGlassBold
                  class="w-5 transition"
                  :class="[
                    searchExpanded
                      ? 'text-theme-primary'
                      : 'text-theme-button-text',
                  ]"
                />
              </div>
              <div
                v-if="searchExpanded"
                class="absolute bottom-0 top-0 flex h-full cursor-pointer items-center dir-ltr:right-0 dir-rtl:left-0"
                @click="
                  () => {
                    search = '';
                    searchInput?.blur();
                  }
                "
              >
                <XCircleBold class="mx-3 w-5 text-theme-primary" />
              </div>
            </div>
          </div>

          <template v-if="!searchExpanded">
            <ADeepCircleButton
              v-if="favoriteProductsStore.favoriteProducts.length > 0"
              :badge="
                favoriteProductsStore.favoriteProducts.length.toLocaleString()
              "
              @click="router.push({ name: 'favorites' })"
            >
              <HeartBold class="h-5 w-5" />
            </ADeepCircleButton>
            <ADeepCircleButton
              v-if="restaurant.forms.length > 0 && isOnline"
              @click="router.push({ name: 'form' })"
            >
              <template #icon><ChatTextBold /></template>
            </ADeepCircleButton>
            <LanguagePickerCircleButton
              v-if="restaurant.languages.length > 1"
            />
            <ADeepCircleButton @click="router.push({ name: 'about' })">
              <template #icon><InfoBold /></template>
            </ADeepCircleButton>
          </template>
        </div>
      </div>

      <div ref="bannerContainer" class="shrink-0 px-4">
        <MenuBanner
          v-if="banner"
          :banner="banner"
          :banner-video="bannerVideo"
        />
      </div>

      <div class="relative">
        <div
          ref="stickyBarElement"
          class="sticky z-40 bg-theme-background"
          style="top: calc(4.25rem + var(--safe-area-inset-top))"
        >
          <div
            class="absolute bottom-0 left-0 right-0 border-t border-theme-text/5"
          ></div>
          <div class="relative mt-3 flex h-12 items-center pb-3">
            <button
              type="button"
              class="relative flex h-full w-12 flex-shrink-0 items-center focus:outline-none"
              @click="categorySidebarExpanded = true"
            >
              <ListBold
                class="w-5 text-theme-primary dir-ltr:ml-4 dir-rtl:mr-4"
              />
            </button>

            <div
              ref="categoryButtonsElement"
              class="scrollbar-none relative flex h-full overflow-x-auto"
            >
              <button
                v-for="category in filteredCategories"
                :key="category.id"
                :ref="
                  (el) =>
                    (categoryButtonElements[category.id] = el as HTMLElement)
                "
                class="relative touch-manipulation whitespace-nowrap rounded-lg px-3 py-1 font-primary font-medium transition dir-ltr:mr-2 dir-rtl:ml-2"
                type="button"
                :class="[
                  category.id === focusedCategoryId
                    ? 'bg-theme-primary text-theme-button-text'
                    : 'bg-theme-text/[0.03] text-theme-text active:bg-theme-text/10',
                ]"
                @click="scrollToCategory(category.id)"
              >
                {{ localize(category.name) }}
              </button>
              <div class="dir-ltr:pl-2 dir-rtl:pr-2">&nbsp;</div>
            </div>
          </div>
        </div>
        <div class="relative flex">
          <CategorySidebar
            :categories="filteredCategories"
            :expanded="categorySidebarExpanded"
            :focused-category-id="focusedCategoryId"
            @hide="categorySidebarExpanded = false"
            @category-click="(category) => scrollToCategory(category.id)"
          />

          <div class="max-w-full flex-1">
            <div
              v-if="filteredCategories.length === 0"
              class="mt-6 px-4 text-center text-theme-text empty:hidden"
            >
              <template v-if="menu.categories.length > 0">
                {{ trans('Nothing found') }}
              </template>
              <template v-else-if="!localize(menu.globalMenuNote)">
                {{ trans('This menu is empty') }}
              </template>
            </div>
            <div>
              <RecentOrders class="px-4 pt-6" />
              <div
                v-if="localize(menu.globalMenuNote)"
                class="break-words px-4 pt-6 [&>p:empty]:h-[1em] [&_a]:underline"
                v-html="localize(menu.globalMenuNote)"
              />
              <section
                v-for="(category, categoryIndex) in filteredCategories"
                :key="category.id"
                :ref="
                  (el) => (categoryElements[category.id] = el as HTMLElement)
                "
                class="px-4 pb-4 pt-6"
                :class="{
                  'min-h-[calc(100vh-210px)]':
                    categoryIndex === filteredCategories.length - 1,
                }"
              >
                <div class="mb-4">
                  <h2 class="break-words font-primary text-xl font-semibold">
                    {{ localize(category.name) }}
                  </h2>

                  <div
                    v-if="
                      localize(category.description) ||
                      localize(menu.globalCategoryNote)
                    "
                    class="mt-1 break-words text-sm text-theme-text"
                  >
                    <div
                      v-if="localize(category.description)"
                      class="[&>p:empty]:h-[1em]"
                    >
                      <p
                        v-for="(
                          categoryDescriptionLine, categoryDescriptionLineIndex
                        ) in (localize(category.description) ?? '').split('\n')"
                        :key="categoryDescriptionLineIndex"
                      >
                        {{ categoryDescriptionLine }}
                      </p>
                    </div>
                    <div
                      v-if="localize(menu.globalCategoryNote)"
                      class="[&>p:empty]:h-[1em] [&_a]:underline"
                      v-html="localize(menu.globalCategoryNote)"
                    ></div>
                  </div>
                </div>

                <ProductCards
                  :products="
                    category.products.filter(
                      (product) =>
                        isCategoryExplicitlyMatchedBySearch(category) ||
                        isProductExplicitlyMatchedBySearch(product),
                    )
                  "
                  :is-first="categoryIndex === 0"
                />
              </section>
            </div>
          </div>
        </div>
      </div>
      <div class="flex-1"></div>
      <div class="flex min-h-[60px] flex-col items-center justify-end">
        <RouterLink :to="{ name: 'about' }" class="text-sm hover:underline">
          {{ restaurant.name }}
        </RouterLink>
        <a
          v-if="!restaurant.hidePoweredBy"
          class="mt-2 rounded-full border border-theme-text/10 px-3 py-1 text-center text-sm transition hover:bg-theme-primary hover:text-theme-button-text"
          :href="`https://apetitomenu.com?utm_source=apetitomenu&utm_medium=menufooter&utm_campaign=${restaurant.slug}`"
        >
          Menu created with <span class="font-semibold">Apetito Menu</span>
        </a>
      </div>
      <div class="sticky bottom-0 z-40 w-full">
        <div
          class="pointer-events-none flex flex-col transition-all"
          style="
            padding-bottom: var(--safe-area-inset-bottom);
            padding-left: var(--safe-area-inset-left);
            padding-right: var(--safe-area-inset-right);
          "
        >
          <div class="mb-4 flex justify-between px-4">
            <div></div>
            <div>
              <Transition
                enter-active-class="ease-out duration-100"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="ease-in duration-100"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <ADeepCircleButton
                  v-if="scrollToTopButtonVisible"
                  class="pointer-events-auto"
                  @click="scrollToTop"
                >
                  <template #icon><ArrowUpBold /></template>
                </ADeepCircleButton>
              </Transition>
            </div>
          </div>
        </div>
        <div
          v-if="
            getSharedData().isOrderMode && cartStore.cartItemsTotalQuantity > 0
          "
          class="mb-4 px-4"
        >
          <RouterLink
            :to="{ name: 'cart' }"
            class="menu-button-primary !flex !items-center !p-3"
          >
            <div
              class="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 font-semibold"
            >
              {{ cartStore.cartItemsTotalQuantity }}
            </div>
            <div class="px-3 font-semibold">{{ trans('View cart') }}</div>
            <div class="flex-1"></div>
            <div
              v-if="cartStore.cartItemsTotalPrice > 0"
              class="px-3 font-semibold"
            >
              {{ formatPrice(cartStore.cartItemsTotalPrice) }}
            </div>
          </RouterLink>
        </div>
        <div
          v-if="menu.isCategoryTypesNavbarEnabled"
          class="flex justify-center px-1"
        >
          <CategoryTypeNavBar />
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import MainLayout from '@menu/Shared/MainLayout.vue';
import ProductCards from '@menu/Shared/ProductCards.vue';
import ADeepCircleButton from '@menu/Shared/ADeepCircleButton.vue';
import LanguagePickerCircleButton from '@menu/Shared/LanguagePickerCircleButton.vue';
import CategorySidebar from '@menu/Shared/CategorySidebar.vue';
import CategoryTypeNavBar from '@menu/Shared/CategoryTypeNavBar.vue';
import RecentOrders from '@menu/Shared/RecentOrders.vue';
import MenuBanner from '@menu/Shared/MenuBanner.vue';
import ArrowLeftBold from '@/phosphoricons/ArrowLeftBold.vue';
import ArrowUpBold from '@/phosphoricons/ArrowUpBold.vue';
import MagnifyingGlassBold from '@/phosphoricons/MagnifyingGlassBold.vue';
import XCircleBold from '@/phosphoricons/XCircleBold.vue';
import ListBold from '@/phosphoricons/ListBold.vue';
import InfoBold from '@/phosphoricons/InfoBold.vue';
import HeartBold from '@/phosphoricons/HeartBold.vue';
import ChatTextBold from '@/phosphoricons/ChatTextBold.vue';
import { useFavoriteProductsStore } from '@menu/Stores/favoriteProducts';
import { trans } from '@menu/Utils/i18n';
import { localize } from '@menu/Utils/i18n';

import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useCurrentMenu } from '@menu/Composables/useCurrentMenu';
import { useEventListener } from '@/composables/useEventListener';
import { useRoute, useRouter } from 'vue-router';
import { CategoryResource } from '@menu/Types/Resources/CategoryResource';
import { ProductResource } from '@menu/Types/Resources/ProductResource';
import { useCartStore } from '@menu/Stores/cart';
import { useIsOnline } from '@menu/Composables/useIsOnline';
import formatPrice from '@menu/Utils/formatPrice';
import getSharedData from '@menu/Utils/getSharedData';
import getRestaurant from '@menu/Utils/getRestaurant';

const route = useRoute();
const router = useRouter();

const searchInput = useTemplateRef('searchInput');

const search = ref('');
watch(search, () => scrollToTop());
const searchFocused = ref(false);
const focusedCategoryId = ref<number | null>(null);
watch(focusedCategoryId, () => scrollToFocusedCategoryButton());
const windowScrollY = ref(0);
const categorySidebarExpanded = ref(false);

const cartStore = useCartStore();
const favoriteProductsStore = useFavoriteProductsStore();
const { menu, availableCategoryTypes } = useCurrentMenu();
const restaurant = getRestaurant();
const { isOnline } = useIsOnline();

const isCategoryNotMatchedByCategoryType = (category: CategoryResource) => {
  const categoryType = route.query.category_type;

  if (!categoryType) return false;

  // safety measure
  if (!availableCategoryTypes.value.some(({ slug }) => slug === categoryType)) {
    return false;
  }

  return category.type !== categoryType;
};

const isCategoryExplicitlyMatchedBySearch = (category: CategoryResource) => {
  if (!normalizedSearch.value) return true;

  return [...Object.values(category.name)].some((name) =>
    normalizeString(name).includes(normalizedSearch.value),
  );
};

const isProductExplicitlyMatchedBySearch = (product: ProductResource) => {
  if (!normalizedSearch.value) return true;

  return [...Object.values(product.name)].some((name) =>
    normalizeString(name).includes(normalizedSearch.value),
  );
};

useEventListener(document, 'scroll', () => {
  detectFocusedCategory();
  windowScrollY.value = window.scrollY;
});

const categoryElements = ref<Record<number, HTMLElement>>({});

const getCategoryEl = (categoryId: number) => {
  return categoryElements.value[categoryId] ?? null;
};

const categoryButtonElements = ref<Record<number, HTMLElement>>({});

const getCategoryButtonEl = (categoryId: number) => {
  return categoryButtonElements.value[categoryId] ?? null;
};

const categoryButtonsElement = useTemplateRef('categoryButtonsElement');

const getCategoryButtonsEl = () => {
  const el = categoryButtonsElement.value;

  return el ? el : null;
};

const headerNavigationContainerElement = useTemplateRef(
  'headerNavigationContainerElement',
);

const getHeaderNavigationContainerHeight = () => {
  if (!headerNavigationContainerElement.value) {
    throw new Error('Header navigation container element is missing.');
  }

  return headerNavigationContainerElement.value.clientHeight;
};

const stickyBarElement = useTemplateRef('stickyBarElement');

const getStickyBarHeight = () => {
  if (!stickyBarElement.value) {
    throw new Error('Sticky bar element is missing.');
  }

  return stickyBarElement.value.clientHeight;
};

const detectFocusedCategory = async () => {
  await nextTick();

  const focusedCategory =
    [...filteredCategories.value].reverse().find((category) => {
      const categoryEl = getCategoryEl(category.id);

      if (!categoryEl) {
        return false;
      }

      return (
        categoryEl.getBoundingClientRect().top <=
        getStickyBarHeight() + getHeaderNavigationContainerHeight()
      );
    }) ?? filteredCategories.value[0];

  focusedCategoryId.value = focusedCategory ? focusedCategory.id : null;
};

onMounted(detectFocusedCategory);

watch(() => route.params.category_type, detectFocusedCategory);

const scrollToFocusedCategoryButton = async () => {
  if (!focusedCategoryId.value) {
    return;
  }

  const categoryButtonEl = getCategoryButtonEl(focusedCategoryId.value);
  const categoryButtonsEl = getCategoryButtonsEl();

  // todo: refactor
  const dir = document.getElementsByTagName('html')[0].dir || 'ltr';

  if (categoryButtonEl && categoryButtonsEl) {
    categoryButtonsEl.scrollTo({
      left:
        dir === 'ltr'
          ? categoryButtonEl.offsetLeft
          : categoryButtonEl.offsetLeft -
            categoryButtonsEl.offsetWidth +
            categoryButtonEl.offsetWidth,
      behavior: 'smooth',
    });
  }
};
const scrollToCategory = (categoryId: number) => {
  const categoryEl = getCategoryEl(categoryId);

  if (categoryEl) {
    window.scrollTo({
      top:
        categoryEl.getBoundingClientRect().top +
        window.scrollY -
        getStickyBarHeight() -
        getHeaderNavigationContainerHeight() +
        1,
      behavior: 'smooth',
    });
  }
};
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
  });
};

const normalizeString = (str: string | null | undefined): string => {
  try {
    str = typeof str === 'string' ? str : '';
    str = str.toLowerCase();
    // remove some special chars
    str = str.replaceAll(/['"“”‘’ /]/g, '');
    // remove repeated chars
    str = str.replaceAll(/(.)\1+/g, (str) => str[0]);
  } catch {
    //
  }

  return str ?? '';
};

const searchExpanded = computed(() => {
  return searchFocused.value || search.value.length > 0;
});

const scrollToTopButtonVisible = computed(() => {
  return windowScrollY.value > 200;
});

const normalizedSearch = computed(() => {
  return normalizeString(search.value);
});

const filteredCategories = computed(() => {
  return menu.value.categories.filter((category) => {
    if (isCategoryNotMatchedByCategoryType(category)) {
      return false;
    }

    if (isCategoryExplicitlyMatchedBySearch(category)) {
      return true;
    }

    if (
      category.products.some((product) =>
        isProductExplicitlyMatchedBySearch(product),
      )
    ) {
      return true;
    }

    return false;
  });
});

watch(filteredCategories, () => scrollToFocusedCategoryButton());

const bannerVideo = computed(() => {
  return menu.value.bannerVideo;
});

const banner = computed(() => {
  return menu.value.banner || restaurant.banner;
});
</script>

<style>
.scrollbar-none::-webkit-scrollbar {
  display: none;
}

.scrollbar-none {
  scrollbar-width: none;
}
</style>
